import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, X, CheckCircle2, Camera, Sun, Focus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel, MaskedLine } from "../components/Reveal";
import { API, IMAGES } from "../constants/business";

const inputCls =
  "w-full bg-black border border-white/10 focus:border-[#E60000] outline-none text-white placeholder:text-zinc-600 px-4 py-3.5 text-sm transition-colors";
const labelCls = "block font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2";

const DAMAGE_TYPES = ["Collision", "Dent", "Scratch", "Bumper Damage", "Paint Damage", "Other"];
const MAX_PHOTOS = 8;

const Field = ({ label, required, children, testId }) => (
  <div data-testid={testId}>
    <label className={labelCls}>
      {label} {required && <span className="text-[#E60000]">*</span>}
    </label>
    {children}
  </div>
);

const Estimate = () => {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_color: "",
    drivable: "yes", damage_type: "Collision", description: "",
    insurance_company: "", claim_number: "", preferred_contact: "phone",
  });
  const [photos, setPhotos] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    document.title = "Submit an Estimate | Mario Auto Body & Collision Repair — Jessup, MD";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (files) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    setPhotos((prev) => {
      const next = [...prev, ...imgs.map((f) => ({ file: f, url: URL.createObjectURL(f) }))];
      if (next.length > MAX_PHOTOS) {
        toast.error(`You can upload up to ${MAX_PHOTOS} photos.`);
        return next.slice(0, MAX_PHOTOS);
      }
      return next;
    });
  };

  const removePhoto = (i) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addPhotos(e.dataTransfer.files);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p.file));
      await axios.post(`${API}/estimates`, fd);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="estimate-page">
      <section className="relative pt-40 pb-20 overflow-hidden">
        <img src={IMAGES.collision} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#E60000] mb-6">Free Estimate Request</p>
          <h1 className="font-display uppercase leading-[0.85] tracking-tight text-6xl md:text-8xl" data-testid="estimate-headline">
            <MaskedLine delay={0.2}>Submit an</MaskedLine>
            <MaskedLine delay={0.35}><span className="text-[#E60000]">Estimate</span></MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-6 max-w-xl text-zinc-300 leading-relaxed"
            data-testid="estimate-subtext"
          >
            Tell us about your vehicle and the damage. Provide as much information as possible so we can better understand your repair needs.
          </motion.p>
        </div>
      </section>

      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.section
            key="confirm"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto px-6 py-24 text-center"
            data-testid="estimate-confirmation"
          >
            <CheckCircle2 size={72} strokeWidth={1.2} className="text-[#E60000] mx-auto mb-8" />
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-none mb-6">Thank You!</h2>
            <p className="text-zinc-300 text-lg leading-relaxed max-w-lg mx-auto">
              Your estimate request has been submitted. We'll review your information and contact you as soon as possible.
            </p>
          </motion.section>
        ) : (
          <motion.section key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* FORM */}
              <form onSubmit={submit} className="lg:col-span-8 border border-white/10 p-6 md:p-10" data-testid="estimate-form">
                <Reveal>
                  <SectionLabel>Your Information</SectionLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                    <Field label="Full Name" required testId="field-full-name">
                      <input data-testid="input-full-name" required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="John Smith" />
                    </Field>
                    <Field label="Phone Number" required testId="field-phone">
                      <input data-testid="input-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="240-555-0123" />
                    </Field>
                    <Field label="Email Address" required testId="field-email">
                      <input data-testid="input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                    </Field>
                    <Field label="Preferred Contact Method" required testId="field-preferred-contact">
                      <div className="flex gap-3">
                        {["phone", "email"].map((m) => (
                          <button
                            type="button"
                            key={m}
                            data-testid={`preferred-contact-${m}`}
                            onClick={() => setForm((f) => ({ ...f, preferred_contact: m }))}
                            className={`flex-1 border px-4 py-3.5 text-sm uppercase tracking-wider transition-colors ${
                              form.preferred_contact === m
                                ? "border-[#E60000] bg-[#E60000]/10 text-white"
                                : "border-white/10 text-zinc-500 hover:border-white/30"
                            }`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>
                </Reveal>

                <Reveal>
                  <SectionLabel>Vehicle Details</SectionLabel>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-12">
                    <Field label="Year" required testId="field-vehicle-year">
                      <input data-testid="input-vehicle-year" required className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2019" />
                    </Field>
                    <Field label="Make" required testId="field-vehicle-make">
                      <input data-testid="input-vehicle-make" required className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Honda" />
                    </Field>
                    <Field label="Model" required testId="field-vehicle-model">
                      <input data-testid="input-vehicle-model" required className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Civic" />
                    </Field>
                    <Field label="Color" testId="field-vehicle-color">
                      <input data-testid="input-vehicle-color" className={inputCls} value={form.vehicle_color} onChange={set("vehicle_color")} placeholder="Silver" />
                    </Field>
                  </div>
                </Reveal>

                <Reveal>
                  <SectionLabel>Damage Details</SectionLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                    <Field label="Is the vehicle drivable?" required testId="field-drivable">
                      <div className="flex gap-3">
                        {["yes", "no"].map((v) => (
                          <button
                            type="button"
                            key={v}
                            data-testid={`drivable-${v}`}
                            onClick={() => setForm((f) => ({ ...f, drivable: v }))}
                            className={`flex-1 border px-4 py-3.5 text-sm uppercase tracking-wider transition-colors ${
                              form.drivable === v
                                ? "border-[#E60000] bg-[#E60000]/10 text-white"
                                : "border-white/10 text-zinc-500 hover:border-white/30"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </Field>
                    <Field label="Type of Damage" required testId="field-damage-type">
                      <select
                        data-testid="select-damage-type"
                        className={`${inputCls} appearance-none`}
                        value={form.damage_type}
                        onChange={set("damage_type")}
                      >
                        {DAMAGE_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-black">{t}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Description of Damage" required testId="field-description">
                    <textarea
                      data-testid="input-description"
                      required
                      rows={5}
                      className={`${inputCls} resize-none mb-12`}
                      value={form.description}
                      onChange={set("description")}
                      placeholder="Describe what happened and where the damage is located (e.g., front driver-side fender dented, scratch along rear bumper)..."
                    />
                  </Field>
                </Reveal>

                <Reveal>
                  <SectionLabel>Insurance (Optional)</SectionLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                    <Field label="Insurance Company" testId="field-insurance-company">
                      <input data-testid="input-insurance-company" className={inputCls} value={form.insurance_company} onChange={set("insurance_company")} placeholder="e.g., GEICO" />
                    </Field>
                    <Field label="Claim Number" testId="field-claim-number">
                      <input data-testid="input-claim-number" className={inputCls} value={form.claim_number} onChange={set("claim_number")} placeholder="If you have one" />
                    </Field>
                  </div>
                </Reveal>

                <Reveal>
                  <SectionLabel>Photos of Damage</SectionLabel>
                  <div
                    data-testid="photo-dropzone"
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => fileInput.current?.click()}
                    className={`cursor-pointer border border-dashed p-10 md:p-14 text-center transition-colors mb-6 ${
                      dragging ? "border-[#E60000] bg-[#E60000]/5" : "border-white/20 hover:border-white/40"
                    }`}
                  >
                    <UploadCloud size={40} strokeWidth={1.2} className="mx-auto mb-4 text-zinc-500" />
                    <p className="text-white font-semibold mb-1">Tap to upload or drag photos here</p>
                    <p className="text-zinc-500 text-sm">Up to {MAX_PHOTOS} photos — JPG or PNG, max 10MB each</p>
                    <input
                      ref={fileInput}
                      data-testid="photo-file-input"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
                    />
                  </div>

                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6" data-testid="photo-preview-grid">
                      {photos.map((p, i) => (
                        <div key={p.url} className="relative group aspect-square border border-white/10 overflow-hidden" data-testid={`photo-preview-${i}`}>
                          <img src={p.url} alt={`Damage ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            data-testid={`photo-remove-${i}`}
                            onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                            className="absolute top-1 right-1 bg-black/80 border border-white/20 p-1.5 text-white hover:bg-[#E60000] transition-colors"
                            aria-label={`Remove photo ${i + 1}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-estimate-request-btn"
                    className="w-full bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider text-base px-10 py-6 transition-colors inline-flex items-center justify-center gap-3"
                  >
                    {submitting && <Loader2 size={18} className="animate-spin" />}
                    {submitting ? "Submitting..." : "Submit Estimate Request"}
                  </button>
                </Reveal>
              </form>

              {/* PHOTO TIPS SIDEBAR */}
              <aside className="lg:col-span-4" data-testid="photo-tips-section">
                <Reveal delay={0.15}>
                  <div className="border border-white/10 bg-[#0A0A0A] p-8 sticky top-28">
                    <div className="relative border border-white/10 mb-8">
                      <img src={IMAGES.paint} alt="Technician spray painting a vehicle panel" className="w-full h-44 object-cover" />
                      <div className="absolute inset-0 bg-black/40" />
                    </div>
                    <h3 className="font-display text-3xl uppercase tracking-wide mb-6">Photo Tips</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-8">
                      Clear photos help us understand your damage faster and give you a more accurate response.
                    </p>
                    <div className="flex flex-col gap-6">
                      {[
                        { icon: Camera, title: "Multiple Angles", desc: "Take photos from the front, side and corner of the damaged area." },
                        { icon: Focus, title: "Close-Up + Wide Shot", desc: "Capture one close-up of the damage and one full shot of the whole panel or vehicle." },
                        { icon: Sun, title: "Good Lighting", desc: "Shoot in daylight or a well-lit area so dents and scratches are clearly visible." },
                      ].map((t) => (
                        <div key={t.title} className="flex gap-4" data-testid={`photo-tip-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          <t.icon size={20} strokeWidth={1.5} className="text-[#E60000] shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-white text-sm mb-1">{t.title}</p>
                            <p className="text-zinc-500 text-sm leading-relaxed">{t.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              </aside>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </main>
  );
};

export default Estimate;
